/**
 * Pure display helpers for tournament game rows (epic 2, Slice 1).
 * Kept separate from the component so the formatting is unit-testable.
 */

import type { TournamentGameDto } from "../../../../types/tournament-games";

/**
 * Format a game's kickoff as a short date + time in the game's own timezone,
 * e.g. "Sat Jul 5, 9:00 AM EDT". Falls back to UTC for an invalid IANA id and
 * to an empty string for an unparseable instant, so a bad row never crashes.
 */
export const formatGameDateTime = (
  game: Pick<TournamentGameDto, "startsAtUtc" | "timeZone">,
  locale: string = "en-US",
): string => {
  const start = new Date(game.startsAtUtc);
  if (Number.isNaN(start.getTime())) {
    return "";
  }
  const format = (timeZone: string) =>
    new Intl.DateTimeFormat(locale, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone,
      timeZoneName: "short",
    }).format(start);
  try {
    return format(game.timeZone || "UTC");
  } catch {
    return format("UTC");
  }
};

/** "Home vs Away" matchup label from the raw team names. */
export const formatMatchup = (
  game: Pick<TournamentGameDto, "homeTeamNameRaw" | "awayTeamNameRaw">,
): string => `${game.homeTeamNameRaw} vs ${game.awayTeamNameRaw}`;
